import { createApiWithNewBasePath } from "@api";

import {
  IAnswerMap,
  IAnswerValue,
  IQuestion,
  IServiceRequestDraft,
  ITaskCreationConfig,
} from "../types";

/**
 * Calls behind `/api/public/*`, per
 * https://linear.app/ringtwice/document/endpoints-fb0d6c9d1328
 *
 * These endpoints are public on purpose: a visitor creates a draft before
 * authenticating, and the draft's `signed_id` is the only handle on it.
 * `claimServiceRequestDraft` is the one exception — see its own note.
 *
 * All of them are live — `API::Public::ServiceRequestDraftsController` answers
 * create/show/update, `service_request_creation_config` answers the config. The
 * one thing still on a fixture is the category tree, which has no public
 * endpoint at all (`services/categories.ts`).
 */

export interface IDraftAnswerPayload {
  question_key: string;
  question_id: number;
  value: IAnswerValue;
}

export interface ICreateDraftPayload {
  description: string;
  p_category_id: number;
  s_category_id?: number;
  photo_attachments?: number[];
}

export interface IUpdateDraftPayload {
  title?: string;
  description?: string;
  location?: string;
  latitude?: number;
  longitude?: number;
  place_name?: string;
  zip_code?: string;
  pro_only?: boolean;
  s_category_id?: number;
  photo_attachments?: number[];
  answers?: IDraftAnswerPayload[];
}

export interface ICreateDraftResponse {
  service_request: IServiceRequestDraft;
  /** The config for the predicted subcategory, saving a second round trip. */
  form_config: ITaskCreationConfig;
  /**
   * Present only when `task_creation_full` is on, and `suggested_s_category_id`
   * is `null` if the model matched no subcategory (RT-4384).
   */
  ai_enrichment?: { suggested_s_category_id: number | null };
}

export const getTaskCreationConfig = async (
  sCategoryId: number
): Promise<ITaskCreationConfig> => {
  const { data } = await createApiWithNewBasePath.get<
    { s_category_id: number },
    ITaskCreationConfig
  >("public/service_request_creation_config", { s_category_id: sCategoryId });

  return data;
};

/**
 * Fetches an existing draft, for resume.
 *
 * Plural, matching the route: `resources :service_request_drafts, only:
 * [:create, :show, :update], param: :public_token` (`config/routes/api.rb:28`).
 * The endpoint doc spells this one singular, which is a typo in the doc — the
 * singular path 404s. A draft the user already published answers 404 too: the
 * controller serves a `draft?` task, which is `waiting? || screened?` — so a
 * draft the last config screen has screened still resumes.
 */
export const getServiceRequestDraft = async (
  publicToken: string
): Promise<IServiceRequestDraft> => {
  const { data } = await createApiWithNewBasePath.get<
    unknown,
    { service_request: IServiceRequestDraft }
  >(`public/service_request_drafts/${publicToken}`);

  return data.service_request;
};

export const createServiceRequestDraft = async (
  payload: ICreateDraftPayload
): Promise<ICreateDraftResponse> => {
  const { data } = await createApiWithNewBasePath.post<
    { service_request: ICreateDraftPayload },
    ICreateDraftResponse
  >("public/service_request_drafts", { service_request: payload });

  return data;
};

/**
 * Saves one screen's answers, and on the last one publishes the request.
 *
 * `validate` is what asks for the publish, and it rides *outside*
 * `service_request` — the controller reads `params[:validate]`, not a permitted
 * attribute. Sent only on the final screen: it runs `ScreenDraft` (which needs
 * every required field and answer present) and then `PublishTask`, so an
 * earlier screen would be screened against a draft that is not finished yet and
 * answer 422.
 *
 * Sent whether or not the visitor is logged in: `claim` refuses a draft that
 * was never screened, so the last config screen has to ask for the screening
 * either way. `PublishTask` runs after it in both cases and no-ops for a
 * visitor — `publishable?` needs a user — so `claim` publishes theirs.
 *
 * Omitted rather than sent as `false`: the controller compares
 * `params[:validate].to_s == 'true'`, so anything else is the same as absent,
 * and absent keeps the request body honest about what it is asking for.
 */
export const updateServiceRequestDraft = async (
  publicToken: string,
  payload: IUpdateDraftPayload,
  { validate = false }: { validate?: boolean } = {}
): Promise<IServiceRequestDraft> => {
  const { data } = await createApiWithNewBasePath.put<
    { service_request: IUpdateDraftPayload; validate?: boolean },
    { service_request: IServiceRequestDraft }
  >(`public/service_request_drafts/${publicToken}`, {
    service_request: payload,
    ...(validate ? { validate: true } : {}),
  });

  return data.service_request;
};

/**
 * Assigns the draft to the signed-in user and publishes it: a draft created by
 * a visitor has no `user_id` — only `create` sets one, from the caller's own
 * session — and `publishable?` needs one, so their request is published here
 * rather than by their last PUT.
 *
 * Authenticated, unlike everything else in this file — by the bearer
 * `createUserSession` stored. 422s on a draft that was never screened, which is
 * what the last config screen's `validate` is for.
 */
export const claimServiceRequestDraft = async (
  signedId: string
): Promise<IServiceRequestDraft> => {
  const { data } = await createApiWithNewBasePath.post<
    { signed_id: string },
    { service_request: IServiceRequestDraft }
  >("service_request_drafts/claim", { signed_id: signedId });

  return data.service_request;
};

/**
 * Saves one screen of a request that already exists — the authed edit endpoint.
 *
 * It never publishes and takes no `validate`: the request is already live. It
 * answers with the same `ServiceRequestSerializer` payload as the draft PUT,
 * which is what lets both modes share one cache entry and one set of screens.
 */
export const updateServiceRequest = async (
  taskId: number,
  payload: IUpdateDraftPayload
): Promise<IServiceRequestDraft> => {
  const { data } = await createApiWithNewBasePath.put<
    { service_request: IUpdateDraftPayload },
    { service_request: IServiceRequestDraft }
  >(`service_requests/${taskId}`, { service_request: payload });

  return data.service_request;
};

/**
 * Turns a screen's form values into the answers array the PUT expects.
 *
 * The inverse of `toAnswerMap`. This reverses settled decision 12 ("requests
 * carry `question_key` only"): the backend looks answers up by their integer
 * `question_id` and skips any answer without one, so a key-only payload
 * persisted nothing while still returning 200. The id has to be a JSON number —
 * `"42"` misses the integer-keyed lookup just as a missing id does.
 *
 * Hence the `questions` argument: an answer map is keyed by question key and
 * carries no id, so the screen's own questions supply it.
 *
 * `undefined` means "this screen did not ask", so it is dropped. `null` means
 * "the user cleared it" and is sent.
 */
export const toAnswerPayload = (
  answers: IAnswerMap,
  questions: IQuestion[]
): IDraftAnswerPayload[] => {
  const idByKey = new Map(questions.map(({ key, id }) => [key, id]));

  return Object.entries(answers)
    .filter(([, value]) => value !== undefined)
    .flatMap(([question_key, value]) => {
      const question_id = idByKey.get(question_key);

      // No id means no question on this screen answers to that key, and the
      // backend would drop the entry silently. Drop it here instead.
      if (question_id == null) return [];

      return [{ question_key, question_id, value: value as IAnswerValue }];
    });
};
